import type { PImage } from '../../engine/aliases'
import type { IDeadish, IEntity } from '../../entities/entitiesUtils'
import { entitiesUtils } from '../../entities/entitiesUtils-sidecar'
import { assert } from '../../lib/assert-sidecar'
import { l } from '../../lib/imports'
import { mathUtils } from '../../lib/mathUtils-sidecar'
import { common } from '../../main/common-sidecar'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import { voyageMessageCrewCarry } from './multi/voyageMessageCrewCarry-sidecar'
import type { IVoyageBody } from './voyageBodies'
import { voyageBodies } from './voyageBodies-sidecar'
import {
  EVoyageCarryableType,
  IVoyageCarryableData,
} from './voyageCarryableData'
import { voyageCarryableData } from './voyageCarryableData-sidecar'
import type { IVoyageCrew } from './voyageCrew'
import { voyageGameStatus_enemyShips } from './voyageGameStatus_enemyShips-sidecar'
import { voyageLayers } from './voyageLayers-sidecar'
import { voyageParticles } from './voyageParticles-sidecar'
import type { IVoyageTilemapPart } from './voyageTilemapPart'
import { voyageWebsockets } from './voyageWebsockets-sidecar'

export interface IVoyageCarryable extends IEntity, IDeadish {
  type: EVoyageCarryableType
  data: IVoyageCarryableData
  image: PImage
  body: IVoyageBody | null
  particleTimer: number
  tile: IVoyageTilemapPart | null
  crew: IVoyageCrew | null

  // Bomb data
  explosionTimer: number
  timeToExplode: number
  flickerTimer: number
  flickerIdx: number
}

const objs: IVoyageCarryable[] = []
export function getAll() {
  return objs
}

export function findById(id: number) {
  return entitiesUtils.findById(id, objs)
}
export function findByIdSafe(id: number) {
  return entitiesUtils.findByIdSafe(id, objs)
}
export function create(
  type: EVoyageCarryableType,
  id: number,
): IVoyageCarryable {
  let data = voyageCarryableData.find(type)
  assert.notNil(data)
  let frame = data.frame

  let image = spritesUtil.createImage(
    voyageLayers.getLayers().shipCrewItem,
    frame,
  )
  image.rotation = data.rotation
  image.tint = data.tint
  spritesUtil.scale(image, data.scale)

  let obj: IVoyageCarryable = {
    id,
    idLoaded: -1,
    isDead: false,
    eType: 'voy-misc',
    image,
    body: null,
    particleTimer: 0,
    type,
    data,
    tile: null,
    crew: null,

    explosionTimer: 0,
    timeToExplode: 0,
    flickerTimer: 0,
    flickerIdx: 0,
  }
  if (
    obj.type === EVoyageCarryableType.Breachbomb ||
    obj.type === EVoyageCarryableType.Firebomb
  ) {
    obj.timeToExplode = 30
  }

  objs.push(obj)

  return obj
}

export function setBody(obj: IVoyageCarryable, body: IVoyageBody | null) {
  obj.body = body
  obj.image.visible = body == null
}

export function moveToXY(
  obj: IVoyageCarryable,
  x: number,
  y: number,
  isFlipped = false,
) {
  // obj.x = x
  // obj.y = y
  obj.image.x = x
  obj.image.y = y

  obj.image.flipX = isFlipped

  if (obj.body) {
    voyageBodies.moveToXY(obj.body, x, y, isFlipped)
  }
}

export function setAtTile(obj: IVoyageCarryable, t: IVoyageTilemapPart | null) {
  if (obj.tile) {
    l.remove(obj.tile.items, (c) => c === obj)
    obj.tile = null
  }
  obj.tile = t
  if (t) {
    setAtCrew(obj, null)
    t.items.push(obj)
    moveToXY(obj, t.x + obj.data.rackOx, t.y + obj.data.rackOy)
  }
}

export function setAtCrew(obj: IVoyageCarryable, cr: IVoyageCrew | null) {
  if (obj.crew) {
    l.remove(obj.crew.items, (c) => c === obj)
    obj.crew = null
  }

  obj.crew = cr

  if (cr) {
    setAtTile(obj, null)
    cr.items.push(obj)
    obj.image.scaleX = obj.data.scale * obj.data.carryScaleX
    obj.image.scaleY = obj.data.scale
    moveToXY(obj, cr.x + obj.data.carryOx, cr.y + obj.data.carryOy)
  }
}

export function kill(obj: IVoyageCarryable) {
  obj.isDead = true
}
export function killAll() {
  l.forEach(objs, (c) => {
    kill(c)
  })
  removeDead()
}

export function updateAll() {
  l.forEach(objs, (c) => {
    update(c)
  })
  removeDead()
}
export function removeDead() {
  entitiesUtils.removeDead(objs, (c) => {
    setAtTile(c, null)
    setAtCrew(c, null)
    c.image.destroy()
  })
}
function update(obj: IVoyageCarryable) {
  let ds = common.getUpdateDelta()

  if (obj.isDead) {
    return
  }

  if (obj.type === EVoyageCarryableType.Coffee) {
    obj.particleTimer += ds
    let steamTime = 0.2
    if (obj.particleTimer > steamTime) {
      obj.particleTimer -= steamTime
      voyageParticles.create('steam', obj.image.x - 6, obj.image.y - 4)
    }
  }
  if (
    obj.type === EVoyageCarryableType.Breachbomb ||
    obj.type === EVoyageCarryableType.Firebomb
  ) {
    obj.explosionTimer += ds

    obj.flickerTimer += ds
    let flickerTime = (obj.timeToExplode - obj.explosionTimer) / 20
    if (obj.flickerTimer > flickerTime) {
      obj.flickerTimer = 0
      obj.flickerIdx++
      // let options = [sprites.voyItemBomb[0], sprites.voyItemBomb2[0]]
      let options = [sprites.voyInkItemBomb1[0], sprites.voyInkItemBomb2[0]]
      let scale = obj.image.scale
      spritesUtil.setPackedFrame(
        obj.image,
        mathUtils.getIndexWrapped(obj.flickerIdx, options),
      )
      obj.image.scale = scale
    }

    if (obj.explosionTimer >= obj.timeToExplode) {
      kill(obj)

      if (voyageWebsockets.getIsHost()) {
        voyageMessageCrewCarry.sendDestroyMessageAndImmediatelyExecute({
          id: obj.id,
        })

        let t = obj.tile
        if (!t) {
          if (obj.crew) {
            t = obj.crew.tile
            // TODO: hurt crew!
          }
        }

        if (t) {
          let chanceFire = 0
          let chanceBreach = 0
          if (obj.type === EVoyageCarryableType.Breachbomb) {
            chanceBreach = 0.5
          }
          if (obj.type === EVoyageCarryableType.Firebomb) {
            chanceFire = 0.5
          }
          // assert.notNil(obj.tile)
          voyageGameStatus_enemyShips.hitByWeaponAt(
            t,
            1,
            chanceFire,
            chanceBreach,
            true,
          )
        }
        // voyageMessageEnemyShipCommand.sendMessageAndImmediatelyExecute({
        //   command: EVoyageEnemyShipCommand.
        // })
      }
    }
  }
}

export function countBombs() {
  let num = 0
  l.forEach(getAll(), (c) => {
    if (
      c.type === EVoyageCarryableType.Breachbomb ||
      c.type === EVoyageCarryableType.Firebomb
    ) {
      num++
    }
  })
  return num
}
